<template>
    <DCard padding>
        <DRow
            class="d-flex justify-content-between"
        >
            <DCol sm="9">
                <span
                    class="h5"
                >
                    {{ __('customers.transactions.total_posting_amount') }}: {{ priceFormat(totalPostingAmount, 2, customer.market_region_id) }}
                </span>
            </DCol>
            <DCol sm="3">
                <DButton
                    v-if="userPermissions.includes('invoice.align_payment_status')"
                    id="btn-sync-diamant"
                    :label="__('customers.transactions.finance_system_sync')"
                    color="primary"
                    :disabled="isInLoadingState"
                    class="float-end"
                    @click="requestManualFinanceSync"
                />
            </DCol>
        </DRow>
        <DRow
            v-if="legacyFinanceSystemAvailable"
            class="mt-3 mb-4"
        >
            <DCol>
                <DFormRow
                    label=""
                    :hint="`${__('customers.transactions.legacy_system_hint')}:
                    ${__('settings.tenant_settings.form.fields.finance_systems.' + legacyFinanceSystem)}`"
                >
                    <DSwitch

                        v-model="filtersData.showLegacySystem"
                        :label="__('customers.transactions.legacy_system')"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol
                sm="2"
                class="pt-1"
            >
                <DDateFormatInput
                    id="date_from"
                    v-model="filtersData.dateFrom"
                    format="YYYY-MM-DD"
                    :label="__('customers.transactions.date_from')"
                    class="mb-0"
                    type="date"
                    @keydown.enter.exact.prevent="filter(1, filtersData)"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DDateFormatInput
                    id="date_to"
                    v-model="filtersData.dateTo"
                    format="YYYY-MM-DD"
                    :label="__('customers.transactions.date_to')"
                    class="mb-0"
                    type="date"
                    @keydown.enter.exact.prevent="filter(1, filtersData)"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="type"
                    v-model="filtersData.type"
                    :label="__('customers.transactions.type')"
                    :options="typeOptions"
                />
            </DCol>
            <DCol
                v-if="financeSystemNetSuite"
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="status"
                    v-model="filtersData.status"
                    :label="__('customers.transactions.netsuite.status')"
                    :options="statusOptions"
                />
            </DCol>
            <DCol
                v-if="financeSystemDiamant"
                sm="1"
                class="pt-1"
            >
                <DSelect
                    id="settled"
                    v-model="filtersData.settled"
                    :label="__('customers.transactions.settled')"
                    :options="settledOptions"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DInput
                    id="posting_amount_from"
                    v-model="filtersData.postingAmountFrom"
                    :label="__('customers.transactions.posting_amount_from')"
                    :placeholder="__('customers.transactions.posting_amount_placeholder_from')"
                    class="mb-0"
                    @keydown.enter.exact.prevent="filter(1, filtersData)"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DInput
                    id="posting_amount_to"
                    v-model="filtersData.postingAmountTo"
                    :label="__('customers.transactions.posting_amount_to')"
                    :placeholder="__('customers.transactions.posting_amount_placeholder_to')"
                    class="mb-0"
                    @keydown.enter.exact.prevent="filter(1, filtersData)"
                />
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol
                sm="3"
                class="pt-1"
            >
                <DInput
                    id="search"
                    v-model="filtersData.search"
                    :label="__('messages.search')"
                    :placeholder="__('customers.transactions.search_placeholder')"
                    class="mb-0"
                    @keydown.enter.exact.prevent="filter(1, filtersData)"
                />
            </DCol>
            <DCol
                sm="3"
                class="d-flex gap-2"
            >
                <DButton
                    :label="__('messages.actions.filter')"
                    @click="filter(1, filtersData)"
                />
                <DButton
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DRow
            id="tableTransactions"
            class="mt-3"
        >
            <DCol>
                <DTable
                    :items="transactions.data"
                    :fields="getFields"
                >
                    <template #posting_amount="{item}">
                        <td
                            class="text-right"
                            :class="{'table-text-color-red': item.posting_amount < 0}"
                        >
                            {{ priceFormat(item.posting_amount, 2, customer.market_region_id) }}
                        </td>
                    </template>
                    <template #settled="{item}">
                        <td>
                            {{ item.settled ? __('customers.transactions.settled_yes') : __('customers.transactions.settled_no') }}
                        </td>
                    </template>
                    <template #text="{item}">
                        <td>
                            {{
                                (item.text !== null ? item.text : '') +
                                    (item.inkasso_text !== null ? ', '+ __('customers.transactions.inkasso_text') + ': '+ item.inkasso_text : '')
                            }}
                        </td>
                    </template>
                    <template #external_id="{item}">
                        <td>
                            {{ item.external_id !== null ? item.external_id : '' }}
                        </td>
                    </template>
                    <template #trans_date="{item}">
                        <td>
                            {{ setDateFormat(item.trans_date, 'YYYY-MM-DD hh:mm') }}
                        </td>
                    </template>
                    <template #timestamp="{item}">
                        <td>
                            {{ setDateFormat(item.timestamp, 'YYYY-MM-DD hh:mm') }}
                        </td>
                    </template>
                    <template #status="{item}">
                        <td>
                            {{ item.status ?? '' }}
                        </td>
                    </template>
                    <template #invoice_number="{item}">
                        <td>
                            {{ item.invoice_number ?? '' }}
                        </td>
                    </template>
                    <template #trans_type="{item}">
                        <td>
                            {{ translateType(item.trans_type) }}
                        </td>
                    </template>
                    <template #reference="{item}">
                        <td>
                            {{ item.reference ?? '' }}
                        </td>
                    </template>
                    <template #memo="{item}">
                        <td>
                            {{ item.memo ?? '' }}
                        </td>
                    </template>
                    <template #transaction_date="{item}">
                        <td>
                            {{ setDateFormatEuropeBerlin(item.transaction_date, 'YYYY-MM-DD hh:mm') }}
                        </td>
                    </template>
                    <template #customer_balance="{item}">
                        <td>
                            {{ priceFormat(item.customer_balance, 2, customer.market_region_id) }}
                        </td>
                    </template>
                    <template #open_amount="{item}">
                        <td>
                            {{ priceFormat(item.open_amount, 2, customer.market_region_id) }}
                        </td>
                    </template>
                    <template #onecrm_id="{item}">
                        <td>
                            {{ item.onecrm_id ?? '' }}
                        </td>
                    </template>

                    <template #applications="{ item }">
                        <td>
                            <DIcon
                                v-if="financeSystemNetSuite && item.applications && item.applications.length"
                                title="Show applications"
                                :size="32"
                                :name="expandedRowsNetsuiteApplications.has(item.id) ? 'chevron-up' : 'chevron-down'"
                                role="button"
                                class="cursor-pointer icon-btn-toggle-netsuite-applications"
                                @click="toggleRow($event, item.id)"
                            />

                            <div
                                v-if="expandedRows[item.id]"
                                :id="`apps-home-${item.id}`"
                                class="d-none"
                            >
                                <DTable
                                    :id="`netsuiteApplications-${item.id}`"
                                    class="netsuite-applications-table d-none"
                                    :items="item.applications"
                                    :fields="fieldsNetsuiteApplications"
                                >
                                    <template #open_amount="{ item: app }">
                                        <td>
                                            {{ priceFormat(app.open_amount, 2, customer.market_region_id) }}
                                        </td>
                                    </template>
                                    <template #amount_applied="{ item: app }">
                                        <td>
                                            {{ priceFormat(app.amount_applied, 2, customer.market_region_id) }}
                                        </td>
                                    </template>
                                    <template #onecrm_id="{ item: app }">
                                        <td>
                                            {{ app.onecrm_id ?? '' }}
                                        </td>
                                    </template>
                                </DTable>
                            </div>
                        </td>
                    </template>
                </DTable>

                <DPagination
                    v-if="transactions.last_page > 1"
                    :active-page="transactions.current_page"
                    :pages="transactions.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <DFabs>
            <s-s-o-fab
                :customer="customer"
                @open-modal="showCreateCloudpitCustomerModal = true"
                @close-modal="showCreateCloudpitCustomerModal = false"
            />
        </DFabs>
        <CreateCloudPitCustomerModal
            :customer="customer"
            :show-modal="showCreateCloudpitCustomerModal"
            @close-modal="showCreateCloudpitCustomerModal = false"
        />
        <ManualFinanceSyncModal
            v-if="showManualFinanceSyncModal"
            :customer="customer"
            :show-modal="showManualFinanceSyncModal"
            :internal-support-link="internalSupportLink"
            :sync-request-id="financeSyncRequestId"
            @close-modal="showManualFinanceSyncModal = false"
            @success="manualFinanceSyncSuccess"
        />
    </DCard>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import SSOFab from '~@/Components/Customers/SSOFab'
import CreateCloudPitCustomerModal from '~@/Components/Customers/CreateCloudPitCustomerModal'
import ManualFinanceSyncModal from '~@/Components/Customers/Transactions/ManualFinanceSyncModal'
import priceFormat from '~@/Mixins/priceFormat'
import _ from 'lodash'

export default {
    name: 'CustomerTransactionsIndex',
    components: {
        CreateCloudPitCustomerModal,
        ManualFinanceSyncModal,
        SSOFab
    },
    mixins: [priceFormat],
    layout: [App, CustomerBase],
    props: {
        totalPostingAmount: {
            type: Number,
            default: 0
        },
        transactions: {
            type: Object,
            default: () => { return {} }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        filters: {
            type: Object,
            default: () => { return {} }
        },
        transactionTypes: {
            type: Array,
            default: () => { return [] }
        },
        transactionStatuses: {
            type: Array,
            default: () => { return [] }
        },
        financeSystem: {
            type: String,
            default: () => { return '' }
        },
        legacyFinanceSystem: {
            type: String,
            default: () => { return '' }
        },
        internalSupportLink: {
            type: String,
            default: () => { return '' }
        },
        // Set after a sync was requested; identifies the queue job request the modal polls.
        financeSyncRequestId: {
            type: Number,
            default: null
        }
    },
    data () {
        return {
            fieldsDiamant: [
                { key: 'trans_date', label: this.__('customers.transactions.date') },
                { key: 'trans_type', label: this.__('customers.transactions.type') },
                { key: 'posting_amount', label: this.__('customers.transactions.posting_amount') },
                { key: 'internal_trans_no', label: this.__('customers.transactions.internal_trans_no') },
                { key: 'text', label: this.__('customers.transactions.text') },
                { key: 'settled', label: this.__('customers.transactions.settled') },
                { key: 'external_id', label: this.__('customers.transactions.paypal_id') }
            ],
            fieldsNetvisor: [
                { key: 'date', label: this.__('customers.transactions.date') },
                { key: 'type', label: this.__('customers.transactions.type') },
                { key: 'posting_amount', label: this.__('customers.transactions.amount') },
                { key: 'reference_number', label: this.__('customers.transactions.reference_number') },
                { key: 'invoice_number', label: this.__('customers.transactions.invoice_number') },
                { key: 'status', label: this.__('customers.transactions.invoice_status') }
            ],
            fieldsAntagonist: [
                { key: 'timestamp', label: this.__('customers.transactions.date') },
                { key: 'type', label: this.__('customers.transactions.type') },
                { key: 'posting_amount', label: this.__('customers.transactions.amount') },
                { key: 'finance_system_id', label: this.__('customers.transactions.reference_number') },
                { key: 'invoice_id', label: this.__('customers.transactions.invoice_number') },
                { key: 'status', label: this.__('customers.transactions.invoice_status') }
            ],
            fieldsPayex360: [
                { key: 'date', label: this.__('customers.transactions.date') },
                { key: 'type', label: this.__('customers.transactions.type') },
                { key: 'posting_amount', label: this.__('customers.transactions.amount') },
                { key: 'invoice_id', label: this.__('customers.transactions.reference_number') },
                { key: 'invoice_id', label: this.__('customers.transactions.invoice_number') },
                { key: 'status', label: this.__('customers.transactions.invoice_status') }
            ],
            fieldsNetsuite: [
                { key: 'request_type', label: this.__('customers.transactions.netsuite.request_type') },
                { key: 'netsuite_id', label: this.__('customers.transactions.netsuite.netsuite_id') },
                { key: 'onecrm_id', label: this.__('customers.transactions.netsuite.onecrm_id') },
                { key: 'transaction_date', label: this.__('customers.transactions.netsuite.transaction_date') },
                { key: 'customer_balance', label: this.__('customers.transactions.netsuite.customer_balance') },
                { key: 'currency', label: this.__('customers.transactions.netsuite.currency') },
                { key: 'posting_amount', label: this.__('customers.transactions.netsuite.posting_amount') },
                { key: 'open_amount', label: this.__('customers.transactions.netsuite.open_amount') },
                { key: 'status', label: this.__('customers.transactions.netsuite.status') },
                { key: 'reference', label: this.__('customers.transactions.netsuite.reference') },
                { key: 'memo', label: this.__('customers.transactions.netsuite.memo') },
                { key: 'applications', label: this.__('customers.transactions.netsuite.applied_to') },
            ],
            fieldsNetsuiteApplications: [
                { key: 'type', label: this.__('customers.transactions.netsuite.request_type') },
                { key: 'netsuite_id', label: this.__('customers.transactions.netsuite.netsuite_id') },
                { key: 'onecrm_id', label: this.__('customers.transactions.netsuite.onecrm_id') },
                { key: 'amount_applied', label: this.__('customers.transactions.netsuite.amount_applied') },
                { key: 'open_amount', label: this.__('customers.transactions.netsuite.open_amount') },
                { key: 'status', label: this.__('customers.transactions.netsuite.status') },
            ],
            filtersData: _.cloneDeep(this.filters),
            showCreateCloudpitCustomerModal: false,
            // Inertia remounts this page when a visit does not preserve state and reuses it when it
            // does, so the modal is opened both here and from the watcher below.
            showManualFinanceSyncModal: this.financeSyncRequestId !== null,
            expandedRowsNetsuiteApplications: new Set(),
            expandedRows: {},
        }
    },
    computed: {
        typeOptions () {
            const typeOptions = this.transactionTypes.map(type => {
                return {
                    value: type,
                    label: this.translateType(type)
                }
            })

            typeOptions.unshift({
                value: '',
                label: ''
            })

            return typeOptions
        },
        statusOptions () {
            const statusOptions = this.transactionStatuses.map(status => {
                return {
                    value: status,
                    label: this.__('customers.transactions.netsuite.statuses.' + status)
                }
            })

            statusOptions.unshift({
                value: '',
                label: ''
            })

            return statusOptions
        },
        settledOptions () {
            const settledOptions = []
            settledOptions.push({ value: null, label: '' })
            settledOptions.push({ value: '1', label: this.__('customers.transactions.settled_yes') })
            settledOptions.push({ value: '0', label: this.__('customers.transactions.settled_no') })

            return settledOptions
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        financeSystemDiamant () {
            return this.financeSystem === 'diamant'
        },
        financeSystemNetSuite () {
            return this.financeSystem === 'netsuite'
        },
        legacyFinanceSystemAvailable () {
            return this.legacyFinanceSystem !== ''
        },
        getFields () {
            switch (this.financeSystem) {
                case 'diamant':
                    return this.fieldsDiamant
                case 'net_visor':
                    return this.fieldsNetvisor
                case 'antagonist_finance':
                    return this.fieldsAntagonist
                case 'payex360':
                    return this.fieldsPayex360
                case 'netsuite':
                    return this.fieldsNetsuite
                default:
                    return []
            }
        },
    },
    watch: {
        'filtersData.showLegacySystem' (val) {
            this.filter(1, { showLegacySystem: val ? 1 : 0 })
        },
        financeSyncRequestId (id) {
            if (id !== null) {
                this.showManualFinanceSyncModal = true
            }
        },
    },
    methods: {
        changePage (page) {
            this.filter(page, this.filtersData)
        },
        filter (page, filters, defaultFilters = false) {
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            const routeParameters = {
                customer: this.customer.id,
                save_filters: true
            }

            queryParameters.showLegacySystem = queryParameters.showLegacySystem ? 1 : 0

            if (page > 1) {
                queryParameters.page = page
            }

            if (defaultFilters) {
                routeParameters.default_filters = true
            }

            this.$inertia.get(this.$route('customers.customers.transactions.index', routeParameters), queryParameters)
        },
        resetFilters () {
            this.filter(1, {}, true)
        },
        requestManualFinanceSync () {
            this.showManualFinanceSyncModal = true
            this.$inertia.post(this.$route('customers.transactions.manual_finance_system_sync', { customer: this.customer.id }, true))
        },
        manualFinanceSyncSuccess () {
            this.showManualFinanceSyncModal = false
            // The sync changed the transactions and the posting amount, so pull the table again. The
            // flash is set afterwards because the reload replaces the page props, which would drop it.
            this.$inertia.reload({
                preserveScroll: true,
                onSuccess: () => {
                    this.$page.props.flash.message = this.__('customers.transactions.success.finance_system_success')
                },
            })
        },
        translateType (type) {
            const translationKey = 'customers.transactions.types.' + type.toLowerCase()
            return this.__(translationKey) === translationKey ? type : this.__(translationKey) + ' (' + type + ')'
        },
        toggleRow (evt, id) {
            this.expandedRows[id] = !this.expandedRows[id]
            this.$nextTick(() => {
                this.injectNetsuiteAppliedToRows(evt, id)
            })
        },
        injectNetsuiteAppliedToRows (evt, id) {
            if (!this.financeSystemNetSuite) {
                return
            }

            const tr = evt.currentTarget.closest('tr')
            const table = document.getElementById(`netsuiteApplications-${id}`)
            const mainTableContainer = document.getElementById('tableTransactions') // original container
            if (!tr || !table || !mainTableContainer) {
                return
            }

            const details = tr.parentNode.querySelector(`tr[data-apps-row="${id}"]`)
            const expanded = new Set(this.expandedRowsNetsuiteApplications)

            if (details) {
                mainTableContainer.appendChild(table)
                table.classList.add('d-none')
                details.remove()
                expanded.delete(id)
                this.expandedRowsNetsuiteApplications = expanded
                return
            }

            // expand
            const detailsTr = document.createElement('tr')
            detailsTr.dataset.appsRow = id

            const td = document.createElement('td')
            td.colSpan = tr.cells?.length || 1

            td.appendChild(table)
            table.classList.remove('d-none')

            detailsTr.appendChild(td)
            tr.after(detailsTr)

            expanded.add(id)
            this.expandedRowsNetsuiteApplications = expanded
        },
    }
}
</script>
<style>
.netsuite-applications-table table {
    font-style: italic !important;
    font-size: 0.8rem !important;
    width: 100% !important;
    margin-bottom: 0 !important;
}

.icon-btn-toggle-netsuite-applications {
    height: 10px !important;
    width: 10px !important;
    vertical-align: middle;
}
.netsuite-applications-table table th {
    border-top: none !important;
}
.netsuite-applications-table table th, .netsuite-applications-table table td {
    padding: 10px !important;
}

</style>
